import { blushColors, eyeshadowColors } from '../../makeuphair/components/services/configs/makeup/cheekEyeshadowColors';
import lipstickConfig from '../../makeuphair/components/services/configs/makeup/lipstick';
import { skinToneColors } from '../../makeuphair/components/services/configs/makeup/skinToneColors';
import { LAB_CATEGORY_ORDER, colorLayer } from './labMakeupSchema';
const PALETTE_NAMES = [
  ...skinToneColors,
  ...blushColors,
  ...eyeshadowColors,
  ...(lipstickConfig.colors || []),
];

function hexName(hex) {
  if (!hex) return 'Custom';
  const hx = normalizeHex(hex);
  const hit = PALETTE_NAMES.find((c) => String(c.hex).toUpperCase() === hx);
  return hit?.name || 'Custom';
}

function normalizeHex(hex) {
  if (!hex) return null;
  const h = String(hex).trim();
  if (!h) return null;
  return h.startsWith('#') ? h.toUpperCase() : `#${h}`.toUpperCase();
}

export function studioFoundationHex(skinAnalysis) {
  return normalizeHex(skinAnalysis?.foundation_hex || skinAnalysis?.skin_profile?.foundation_hex);
}

/** One palette step lighter than foundation (for concealer). */
export function concealerHexFromFoundation(foundationHex) {
  const f = normalizeHex(foundationHex);
  if (!f) return null;
  const list = skinToneColors.map((c) => c.hex.toUpperCase());
  const idx = list.indexOf(f);
  if (idx > 0) return list[idx - 1];
  if (idx === 0) return list[0];
  return f;
}

function formatDepth(depth) {
  if (!depth) return 'guest';
  return String(depth).replace(/_/g, ' ');
}

export function isLayerActive(layer) {
  if (!layer || typeof layer !== 'object') return false;
  if (layer.disabled || layer.none) return false;
  if (String(layer.colorName || '').trim().toLowerCase() === 'none') return false;
  return Number(layer.intensity) > 0;
}

/** Picker hex must win over stale autoBlush / autoLip / autoEyeshadow from presets. */
export function stripStaleAutoFlags(layer, category) {
  if (!layer || typeof layer !== 'object') return layer;
  const hx = normalizeHex(layer.color);
  if (!hx) return { ...layer };
  const out = { ...layer, color: hx };
  if (category === 'blush') delete out.autoBlush;
  if (category === 'lipstick') delete out.autoLip;
  if (category === 'eyeshadow') delete out.autoEyeshadow;
  if (category === 'foundation' && hx) delete out.autoSkin;
  if (category === 'concealer' && hx) {
    delete out.autoSkinConcealer;
    delete out.autoSkin;
  }
  return out;
}

function categoryNeedsStudioResolve(cat, layer) {
  if (!isLayerActive(layer)) return false;
  if (cat === 'foundation') return Boolean(layer.autoSkin);
  if (cat === 'concealer') return Boolean(layer.autoSkinConcealer || layer.autoSkin);
  if (cat === 'blush') return Boolean(layer.autoBlush);
  if (cat === 'lipstick') return Boolean(layer.autoLip);
  if (cat === 'eyeshadow') return Boolean(layer.autoEyeshadow);
  return false;
}

const EXPLICIT_COLOR_CATEGORIES = new Set([
  'blush',
  'eyeshadow',
  'lipstick',
  'highlighter',
  'foundation',
  'concealer',
]);

function layerFinish(layer) {
  return { finishName: layer?.finishName, finishType: layer?.finishType };
}

/** Prompt/API lab from custom picks only; studio supplies hex for Auto* categories. */
export function buildEffectiveMakeupLab(customLab, studioLab, skinAnalysis) {
  const out = {};
  if (!customLab || typeof customLab !== 'object') return out;
  const fHex = studioFoundationHex(skinAnalysis);

  for (const cat of LAB_CATEGORY_ORDER) {
    const layer = customLab[cat];
    if (!isLayerActive(layer)) continue;

    const explicitHex = normalizeHex(layer.color);
    if (
      explicitHex &&
      EXPLICIT_COLOR_CATEGORIES.has(cat) &&
      !(
        (cat === 'foundation' && layer.autoSkin) ||
        (cat === 'concealer' && (layer.autoSkinConcealer || layer.autoSkin))
      )
    ) {
      const cleaned = stripStaleAutoFlags(layer, cat);
      out[cat] = {
        ...cleaned,
        color: explicitHex,
        colorName: cleaned.colorName || hexName(explicitHex),
      };
      continue;
    }

    if (cat === 'foundation' && layer.autoSkin && fHex) {
      out[cat] = colorLayer(hexName(fHex), fHex, layer.intensity, layerFinish(layer));
      continue;
    }
    if (cat === 'concealer' && (layer.autoSkinConcealer || layer.autoSkin)) {
      const cHex =
        studioLab?.concealer?.color || concealerHexFromFoundation(fHex || studioLab?.foundation?.color);
      if (cHex) {
        out[cat] = colorLayer(`${hexName(cHex)} (lift)`, cHex, layer.intensity, layerFinish(layer));
      }
      continue;
    }
    if (layer.autoBlush && studioLab?.blush) {
      out[cat] = { ...studioLab.blush, intensity: layer.intensity, ...layerFinish(layer) };
      continue;
    }
    if (layer.autoLip && studioLab?.lipstick) {
      out[cat] = { ...studioLab.lipstick, intensity: layer.intensity, ...layerFinish(layer) };
      continue;
    }
    if (layer.autoEyeshadow && studioLab?.eyeshadow) {
      out[cat] = { ...studioLab.eyeshadow, intensity: layer.intensity, ...layerFinish(layer) };
      continue;
    }
    if (layer.template || layer.styleName) {
      out[cat] = { ...layer };
      continue;
    }
    const hx = normalizeHex(layer.color);
    out[cat] = {
      ...layer,
      ...(hx ? { color: hx, colorName: layer.colorName || hexName(hx) } : {}),
    };
  }
  return out;
}

/** After studio /resolve, sync only Auto* categories — never overwrite manual swatch picks. */
export function hydrateCustomLabFromResolved(customLab, studioLab, skinAnalysis) {
  if (!customLab || !studioLab || !skinAnalysis?.success) return customLab;
  const next = { ...customLab };
  for (const cat of LAB_CATEGORY_ORDER) {
    const layer = customLab[cat];
    if (!categoryNeedsStudioResolve(cat, layer)) continue;
    const partial = buildEffectiveMakeupLab({ [cat]: layer }, studioLab, skinAnalysis);
    const resolved = partial[cat];
    if (!resolved) continue;
    next[cat] = { ...resolved };
    if (layer.template) next[cat].template = layer.template;
    if (layer.styleName) next[cat].styleName = layer.styleName;
  }
  return next;
}

/** @deprecated Use buildEffectiveMakeupLab — studio as base re-added categories set to None. */
export function mergeMakeupLabSources(studioLab, presetLab, customLab) {
  const base =
    studioLab && Object.keys(studioLab).length ? { ...studioLab } : { ...(presetLab || {}) };
  if (!customLab || typeof customLab !== 'object') return base;
  for (const [key, layer] of Object.entries(customLab)) {
    if (!isLayerActive(layer)) {
      delete base[key];
      continue;
    }
    if (key === 'foundation' && layer.autoSkin && base.foundation?.color) {
      base[key] = {
        ...colorLayer(hexName(base.foundation.color), base.foundation.color, layer.intensity || 18, {
          finishName: layer.finishName || base.foundation.finishName,
          finishType: layer.finishType || base.foundation.finishType,
        }),
      };
      continue;
    }
    if (layer.color) {
      base[key] = {
        ...colorLayer(hexName(layer.color), normalizeHex(layer.color), layer.intensity, {
          finishName: layer.finishName,
          finishType: layer.finishType,
        }),
      };
      continue;
    }
    base[key] = { ...layer };
  }
  return base;
}

export function stripInactiveLayers(makeupLab) {
  if (!makeupLab || typeof makeupLab !== 'object') return {};
  const out = {};
  for (const [key, layer] of Object.entries(makeupLab)) {
    if (key.startsWith('_')) continue;
    if (isLayerActive(layer)) out[key] = { ...layer };
  }
  return out;
}

export function resolveMakeupLabForSkin(makeupLab, skinAnalysis) {
  const base = stripInactiveLayers(makeupLab);
  if (!skinAnalysis?.success) return base;

  const out = { ...base };
  const fHex = studioFoundationHex(skinAnalysis);

  if (out.foundation?.autoSkin && fHex) {
    const { autoSkin, ...rest } = out.foundation;
    out.foundation = colorLayer(hexName(fHex), fHex, rest.intensity || 18, {
      finishName: rest.finishName,
      finishType: rest.finishType,
    });
  } else if (out.foundation?.color) {
    const hx = normalizeHex(out.foundation.color);
    out.foundation = {
      ...out.foundation,
      color: hx,
      colorName: hexName(hx) || out.foundation.colorName,
    };
  } else if (out.foundation) {
    delete out.foundation;
  }

  if (out.concealer?.autoSkinConcealer) {
    const cHex = concealerHexFromFoundation(fHex || out.foundation?.color);
    if (cHex) {
      const { autoSkinConcealer, ...rest } = out.concealer;
      out.concealer = colorLayer(`${hexName(cHex)} (lift)`, cHex, rest.intensity || 14, {
        finishName: rest.finishName,
        finishType: rest.finishType,
      });
    }
  } else if (out.concealer?.color) {
    const hx = normalizeHex(out.concealer.color);
    out.concealer = { ...out.concealer, color: hx, colorName: hexName(hx) || out.concealer.colorName };
  }

  // Blush / lip / shadow hex come from studio /resolve (Gemma + palette); keep layer if already resolved.

  return stripInactiveLayers(out);
}

export function buildSkinTonePromptBlock(skinAnalysis, makeupLab) {
  if (!skinAnalysis?.success) return '';
  const depth = formatDepth(skinAnalysis.skin_profile?.depth);
  const undertone = skinAnalysis.skin_profile?.undertone;
  const lines = [`SKIN TONE (MANDATORY): Gemma/studio depth "${depth}".`];
  if (undertone) {
    lines.push(`Undertone ${undertone} (Gemma vision).`);
  }
  const hasFoundation = isLayerActive(makeupLab?.foundation);
  const hasConcealer = isLayerActive(makeupLab?.concealer);
  const fHex = makeupLab?.foundation?.color || studioFoundationHex(skinAnalysis);
  if (hasFoundation && fHex) {
    const fromPicker = Boolean(makeupLab?.foundation?.color) && !makeupLab?.foundation?.autoSkin;
    lines.push(
      fromPicker
        ? `Foundation ${hexName(fHex)} (${fHex}) — USE THIS EXACT HEX on face at stated strength; visible base color; face must not be lighter than neck.`
        : `Foundation ${hexName(fHex)} (${fHex}) — visible base matching guest undertone; exact depth on face; not lighter than neck.`,
    );
  }
  if (hasConcealer && makeupLab?.concealer?.color) {
    lines.push(
      `Concealer ${makeupLab.concealer.colorName || 'lift'} (${makeupLab.concealer.color}) — under-eye only.`,
    );
  }
  if (!hasFoundation && !hasConcealer) {
    lines.push(
      'NO FACE BASE: Do not add foundation, concealer, powder, tint, or smoothing — leave photographed skin unchanged except listed makeup categories (lips, lids, liner, lashes, brows only when listed).',
    );
    lines.push(
      'Face depth must match bare neck and ears — same melanin, pores, and natural shine; never a lighter or matte face mask.',
    );
  }
  lines.push(
    'Never whiten, bleach, or cool-shift the face vs neck, ears, chest, or hands. Keep natural melanin and warmth.',
  );
  return lines.join(' ');
}
